import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Contexto de seleção. Permite manter seleções separadas por escopo
 * (listagem geral, listagem dentro de uma vaga, dentro de um evaluation group).
 */
export type SelectionContext =
  | { kind: "global" }
  | { kind: "vaga"; id: string }
  | { kind: "evaluationGroup"; id: string };

function contextKey(ctx: SelectionContext): string {
  if (ctx.kind === "vaga") return `vaga:${ctx.id}`;
  if (ctx.kind === "evaluationGroup") return `evaluationGroup:${ctx.id}`;
  return "global";
}

const STORAGE_KEY = "freelancers.selection";

type SelectionStore = Record<string, string[]>;

function readStore(): SelectionStore {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SelectionStore) : {};
  } catch {
    return {};
  }
}

function writeStore(store: SelectionStore) {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

/**
 * Multi-seleção persistente em sessionStorage. Seleções vivem apenas até a aba fechar
 * — não queremos ressuscitar seleções antigas em uma sessão totalmente nova.
 */
export function useFreelancersSelection(context: SelectionContext) {
  const key = useMemo(() => contextKey(context), [context]);
  const [selected, setSelected] = useState<Set<string>>(() => {
    const store = readStore();
    return new Set(store[key] ?? []);
  });

  // Quando o contexto muda (mudou de vaga, voltou ao global), recarrega
  const lastKeyRef = useRef(key);
  useEffect(() => {
    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      const store = readStore();
      setSelected(new Set(store[key] ?? []));
    }
  }, [key]);

  const persist = useCallback(
    (next: Set<string>) => {
      const store = readStore();
      if (next.size === 0) delete store[key];
      else store[key] = Array.from(next);
      writeStore(store);
    },
    [key],
  );

  const toggle = useCallback(
    (id: string) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const set = useCallback(
    (id: string, value: boolean) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (value) next.add(id);
        else next.delete(id);
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const selectMany = useCallback(
    (ids: string[]) => {
      setSelected((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const clear = useCallback(() => {
    setSelected(new Set());
    persist(new Set());
  }, [persist]);

  const replace = useCallback(
    (ids: string[]) => {
      const next = new Set(ids);
      setSelected(next);
      persist(next);
    },
    [persist],
  );

  return {
    selected,
    isSelected: (id: string) => selected.has(id),
    toggle,
    set,
    selectMany,
    clear,
    replace,
    size: selected.size,
  };
}
